/**
 * Expense Query Repository
 *
 * Handles all read operations for expenses.
 * This repository is responsible for fetching expense data from the database.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Expense } from '@/shared/types';
import { mapDatabaseExpenseToExpense } from '@/shared/services/dataMapper';

/**
 * Database Expense entity
 */
interface DatabaseExpense {
  id: string;
  user_id: string;
  category: 'Software' | 'Equipment' | 'Marketing' | 'Office' | 'Travel' | 'Other';
  description: string;
  amount: number;
  date: string;
  receipt_url?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  currency?: string;
  is_billable?: boolean;
  client_id?: string;
}

/**
 * Expense Query Repository
 * Provides read-only operations for the expenses table
 */
export class ExpenseQueryRepository extends BaseRepository<Expense> {
  protected tableName = 'expenses' as const;

  /**
   * Find all expenses for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      let query = db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId);

      if (options?.filters) {
        query = this.applyFilters(query, options.filters);
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? false });
      } else {
        query = query.order('date', { ascending: false });
      }

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: expenses,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses'),
      };
    }
  }

  /**
   * Find a single expense by ID
   */
  async findById(id: string): Promise<RepositoryResult<Expense>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Expense not found',
        };
      }

      const expense = mapDatabaseExpenseToExpense(data as DatabaseExpense);

      return {
        success: true,
        data: expense,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expense'),
      };
    }
  }

  /**
   * Find expenses by user ID (returns array, not paginated)
   * This is a convenience method that returns all expenses for a user
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Expense[]>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      return {
        success: true,
        data: expenses,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by user'),
      };
    }
  }

  /**
   * Get expenses by category
   */
  async getByCategory(
    userId: string,
    category: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('category', category)
        .order('date', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: expenses,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by category'),
      };
    }
  }

  /**
   * Get expenses by date range
   */
  async getByDateRange(
    userId: string,
    startDate: string,
    endDate: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: expenses,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by date range'),
      };
    }
  }

  /**
   * Get expenses by client
   */
  async getByClient(
    userId: string,
    clientId: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('client_id', clientId)
        .order('date', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: expenses,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by client'),
      };
    }
  }

  /**
   * Get expenses by billable status
   */
  async getByBillableStatus(
    userId: string,
    isBillable: boolean,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('is_billable', isBillable)
        .order('date', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const expenses = (data || []).map((dbExpense) =>
        mapDatabaseExpenseToExpense(dbExpense as DatabaseExpense)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: expenses,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expenses by billable status'),
      };
    }
  }
}

// Export singleton instance
export const expenseQueryRepository = new ExpenseQueryRepository();
